import { useState, useCallback, useRef, useEffect } from "react";
import { useTimeline } from "./hooks/useTimeline";
import Timeline from "./components/Timeline";
import Toolbar from "./components/Toolbar";
import EventEditor from "./components/EventEditor";
import { buildTimelineSvg, downloadSvg, printSvg, slugifyFilename } from "./utils/exportSvg";
import "./App.css";

function App() {
  const {
    timelines,
    activeTimelineId,
    activeTimeline,
    events,
    orientation,
    setActiveTimelineId,
    setOrientation,
    addEvent,
    updateEvent,
    deleteEvent,
    addTimeline,
    renameTimeline,
    deleteTimeline,
    importData,
  } = useTimeline();

  const [editingEvent, setEditingEvent] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const renameInputRef = useRef(null);

  useEffect(() => {
    if (renaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renaming]);

  const handleAddEvent = useCallback(() => {
    setEditingEvent(null);
    setShowAddModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setEditingEvent(null);
    setShowAddModal(false);
  }, []);

  const handleSaveEvent = useCallback(
    (eventData) => {
      if (editingEvent) {
        updateEvent(editingEvent.id, eventData);
      } else {
        addEvent(eventData);
      }
      handleCloseModal();
    },
    [editingEvent, addEvent, updateEvent, handleCloseModal],
  );

  const handleDeleteEvent = useCallback(
    (eventId) => {
      deleteEvent(eventId);
      handleCloseModal();
    },
    [deleteEvent, handleCloseModal],
  );

  const handleAddTimeline = useCallback(() => {
    const name = prompt("Enter timeline name:");
    if (name && name.trim()) {
      addTimeline(name.trim());
    }
  }, [addTimeline]);

  const startRename = useCallback(() => {
    setRenameValue(activeTimeline?.name || "");
    setRenaming(true);
  }, [activeTimeline?.name]);

  const commitRename = useCallback(() => {
    if (activeTimelineId && renameValue.trim()) {
      renameTimeline(activeTimelineId, renameValue.trim());
    }
    setRenaming(false);
  }, [activeTimelineId, renameValue, renameTimeline]);

  const cancelRename = useCallback(() => {
    setRenaming(false);
  }, []);

  const handleImport = useCallback(
    (data) => {
      importData(data);
    },
    [importData],
  );

  const handleEventClick = useCallback(
    (treeNode) => {
      const fullEvent = events.find((e) => e.id === treeNode.eventId);
      if (fullEvent) {
        setEditingEvent(fullEvent);
        setShowAddModal(true);
      }
    },
    [events],
  );

  const handleToggleOrientation = useCallback(() => {
    setOrientation(orientation === "vertical" ? "horizontal" : "vertical");
  }, [orientation, setOrientation]);

  const handleExportJson = useCallback(() => {
    return { timelines, activeTimelineId, orientation };
  }, [timelines, activeTimelineId, orientation]);

  const handleExportSvg = useCallback(() => {
    const name = activeTimeline?.name || "Untitled";
    const svg = buildTimelineSvg({ events, orientation, title: name });
    downloadSvg(svg, slugifyFilename(name));
  }, [events, orientation, activeTimeline?.name]);

  const handlePrint = useCallback(() => {
    const name = activeTimeline?.name || "Untitled";
    const svg = buildTimelineSvg({ events, orientation, title: name });
    printSvg(svg, name);
  }, [events, orientation, activeTimeline?.name]);

  return (
    <div className="app">
      <header className="app-header">
        <h1 className="app-title">
          <span className="app-logo" aria-hidden="true" />
          Linr
        </h1>
        {renaming ? (
          <input
            ref={renameInputRef}
            className="app-subtitle-input"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") cancelRename();
            }}
            aria-label="Rename timeline"
          />
        ) : (
          <button type="button" className="app-subtitle" onClick={startRename} title="Rename timeline">
            {activeTimeline?.name || "Untitled"}
            <svg className="app-subtitle-edit" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </button>
        )}
      </header>

      <main className="app-main">
        <Timeline events={events} orientation={orientation} expanded={expanded} onEventClick={handleEventClick} />
      </main>

      <Toolbar
        onAddEvent={handleAddEvent}
        onAddTimeline={handleAddTimeline}
        timelines={timelines}
        activeTimelineId={activeTimelineId}
        onSwitchTimeline={setActiveTimelineId}
        onDeleteTimeline={deleteTimeline}
        onImport={handleImport}
        onExportJson={handleExportJson}
        onExportSvg={handleExportSvg}
        onPrint={handlePrint}
        orientation={orientation}
        onToggleOrientation={handleToggleOrientation}
        expanded={expanded}
        onToggleExpand={() => setExpanded((v) => !v)}
      />

      {showAddModal && <EventEditor event={editingEvent} events={events} onSave={handleSaveEvent} onDelete={handleDeleteEvent} onClose={handleCloseModal} />}
    </div>
  );
}

export default App;
